import {
  type AfterViewChecked,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Injectable,
  Input,
} from '@angular/core';
import type {
  AbstractControl,
  FormGroupDirective,
  NgForm,
} from '@angular/forms';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import type { FloatLabelType } from '@angular/material/form-field';

/**
 * Lets the host force the field into Material's error state.
 *
 * `matInput` already owns `aria-invalid`, deriving it from `errorState`. This
 * component used to write the attribute itself from its `ariaInvalid` input,
 * which meant two writers on one attribute and an order that differs between
 * the first render and later updates: matInput won on first paint, the template
 * won afterwards. A field created ALREADY invalid therefore announced itself as
 * valid, and only the ordinary "becomes invalid while on screen" flow worked —
 * which is why it went unnoticed.
 *
 * Feeding matInput instead of fighting it removes the race, and `errorState`
 * also drives Material's error styling, so the visual and the assistive-tech
 * state can no longer drift apart. Control validity is deliberately untouched:
 * this makes a field *present* as errored without making a parent form invalid.
 */
@Injectable()
export class ForcedErrorStateMatcher extends ErrorStateMatcher {
  /** Set from the component's `ariaInvalid` input. */
  forced = false;

  override isErrorState(
    control: AbstractControl | null,
    form: FormGroupDirective | NgForm | null
  ): boolean {
    return this.forced || super.isErrorState(control, form);
  }
}

@Component({
  selector: 'lib-common-ui-components-text-input',
  templateUrl: './common-ui-components-text-input.component.html',
  styleUrls: ['./common-ui-components-text-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CommonUiComponentsTextInputComponent),
      multi: true,
    },
    // Scoped to this component, so matInput in the template below resolves it
    // from the node injector and nothing else in the app is affected.
    { provide: ErrorStateMatcher, useClass: ForcedErrorStateMatcher },
  ],
  standalone: false,
})
export class CommonUiComponentsTextInputComponent
  implements ControlValueAccessor, AfterViewChecked
{
  constructor(
    private changeDetector: ChangeDetectorRef,
    private errorStateMatcher: ErrorStateMatcher
  ) {}

  @Input() value = '';

  @Input() placeholder = '';

  @Input() label = '';

  @Input() disabled = false;

  @Input() readonly = false;

  @Input() textArea = false;

  @Input() textAreaMinRows = 1;

  @Input() textAreaMaxRows = 5;

  @Input() iconSuffix!: string;

  @Input() iconPrefix!: string;

  @Input() required = false;

  /**
   * Force the field into Material's error state — for hosts that render their
   * own error message beside the field rather than a `mat-error` inside it.
   *
   * Drives `errorState` rather than writing `aria-invalid` directly, so
   * matInput remains the single owner of the attribute AND applies its error
   * styling. Leaving it unset changes nothing: matInput's normal
   * invalid-and-touched rule still applies, exactly as for every other consumer.
   */
  @Input()
  set ariaInvalid(value: boolean | undefined) {
    this.#ariaInvalid = value;
    (this.errorStateMatcher as ForcedErrorStateMatcher).forced = value === true;
  }
  get ariaInvalid(): boolean | undefined {
    return this.#ariaInvalid;
  }
  #ariaInvalid?: boolean;

  @Input() hideRequiredMarker = false;

  @Input() floatLabel!: FloatLabelType;

  @Input() size?: string; // Accepts percentage (e.g., '100%') or px (e.g., '400px')

  @Input() height?: string; // Accepts height values (e.g., '40px', '50px')

  @Input() disableLastPass = true; // Disable LastPass autocomplete by default

  @Input() backgroundColor?: string; // Background color for the input field

  @Input() showBorder = false; // Whether to show border around the input

  @Input() iconColor?: string; // Color for the prefix/suffix icon

  ngAfterViewChecked(): void {
    this.changeDetector.detectChanges();
  }

  onChange = (value: unknown) => {
    // No-op: This will be replaced by Angular's ControlValueAccessor
  };

  onTouched = () => {
    // No-op: This will be replaced by Angular's ControlValueAccessor
  };

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(value: string) {
    this.value = value;
  }

  /**
   * Handle input value changes
   * Note: onTouched() is intentionally NOT called here - it should only be called on blur
   * to properly support Angular form validation states (touched vs dirty)
   */
  onInputChanged($event: string | Event) {
    const value =
      typeof $event === 'string'
        ? $event
        : ($event.target as HTMLInputElement | HTMLTextAreaElement)?.value ||
          '';
    this.value = value;
    this.onChange(value);
  }

  /**
   * Handle input blur event
   * Marks the form control as "touched" which triggers validation display in parent forms.
   * This enables the standard Angular pattern where validation errors appear after:
   * 1. User focuses into the field and blurs out (touched)
   * 2. User types and then deletes content (dirty)
   * 3. User clicks Search/Submit button (programmatic markAsTouched)
   */
  onBlur() {
    this.onTouched();
  }
}
